/** @format */

import React, { useState, useEffect } from "react";
import {
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { Theme, styled } from "@mui/material/styles";
import logoImage from "../assets/images/logo.png";
import appStoreBadge from "../assets/images/app-store-download-button-in-white.png";
import playStoreBadge from "../assets/images/google-play-store-download-button-in-white.png";
import { Link, useLocation } from "react-router-dom";
import { trackEvent, trackOutboundLink, EventNames } from "../utils/analytics";
import { APP_STORE_URLS, STORE_TYPES } from "../utils/constants";
import { Box } from "./MuiCompat";

const fontInter = `'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif`;

interface HeaderProps {
  backgroundColor: string;
}

const navItems = [
  { to: "/", label: "Home" },
  { to: "/blog", label: "Blog" },
  { to: "/news", label: "News" },
  { to: "/about", label: "About Us" },
  { to: "/contact", label: "Contact" },
];

const BADGE_HEIGHT_DESKTOP = 60;
const BADGE_HEIGHT_DRAWER = 64;
// PNGs have ~13% extra transparent padding on the bottom vs the top,
// so the visible pill sits above the img's centerline. Push it down.
const BADGE_VISUAL_OFFSET_RATIO = 0.07;

// Styled DOM elements used in place of <Box> for the header wrapper and
// pill — bypasses MUI Box's prop-union, which blows past TypeScript's
// union complexity limit once @types/three is in scope.
const HeaderRoot = styled("header")(({ theme }) => ({
  position: "fixed",
  top: 12,
  left: 0,
  right: 0,
  zIndex: theme.zIndex.appBar,
  display: "flex",
  justifyContent: "center",
  pointerEvents: "none",
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  [theme.breakpoints.up("md")]: {
    top: 16,
  },
}));

interface PillProps {
  pillBg: string;
  pillBorder: string;
  pillShadow: string;
}

const Pill = styled("div", {
  shouldForwardProp: (prop) =>
    prop !== "pillBg" && prop !== "pillBorder" && prop !== "pillShadow",
})<PillProps>(({ theme, pillBg, pillBorder, pillShadow }) => ({
  pointerEvents: "auto",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(3),
  width: "auto",
  maxWidth: "calc(100vw - 32px)",
  height: 56,
  paddingLeft: theme.spacing(2.25),
  paddingRight: theme.spacing(2.25),
  // Fully rounded pill — matches the App Store / Google Play badges sitting
  // inside it, so the whole header reads as one cohesive stadium shape.
  borderRadius: 999,
  background: pillBg,
  border: pillBorder,
  // Higher saturation amplifies whatever colour is behind the pill, so it
  // glassily picks up the page background's cyan/violet atmosphere.
  backdropFilter: "saturate(220%) blur(22px)",
  WebkitBackdropFilter: "saturate(220%) blur(22px)",
  boxShadow: pillShadow,
  transition: "background 200ms ease, border-color 200ms ease",
  [theme.breakpoints.up("md")]: {
    height: 64,
    paddingLeft: "16px",
    paddingRight: "16px",
  },
  [theme.breakpoints.up("lg")]: {
    gap: theme.spacing(4),
  },
}));

const Header: React.FC<HeaderProps> = ({ backgroundColor }) => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );

  const isLightBackground = backgroundColor === "#fff8f2";
  const textColorPrimary = isLightBackground ? "#1a1a1a" : "#FFFFFF";
  const textColorMuted = isLightBackground ? "rgba(26,26,26,0.62)" : "#9C9C9D";
  const textColorActive = isLightBackground ? "#1a1a1a" : "#FFFFFF";
  const logoFilter = isLightBackground
    ? "invert(1) hue-rotate(180deg)"
    : "none";

  // The pill is "glass tinted by the page bg": a vertical gradient that
  // borrows a hint of the cyan top-glow and the violet bottom-bloom from
  // PageBackground so the header feels embedded in the world rather than
  // sitting on top of it.
  const pillBg = isLightBackground
    ? "linear-gradient(180deg, rgba(255, 255, 255, 0.55) 0%, rgba(255, 248, 242, 0.65) 100%)"
    : "linear-gradient(180deg, rgba(86, 194, 255, 0.07) 0%, rgba(7, 9, 14, 0.55) 50%, rgba(82, 48, 145, 0.08) 100%)";
  const pillBorder = isLightBackground
    ? "1px solid rgba(26, 26, 26, 0.08)"
    : "1px solid rgba(255, 255, 255, 0.10)";
  const pillShadow = isLightBackground
    ? "0 8px 28px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.6)"
    : [
        "0 14px 40px rgba(0, 0, 0, 0.5)",
        "0 0 60px rgba(86, 194, 255, 0.05)",
        "inset 0 1px 0 rgba(255, 255, 255, 0.10)",
        "inset 0 -1px 0 rgba(86, 194, 255, 0.10)",
      ].join(", ");

  useEffect(() => {
    if (!isMobile && mobileOpen) setMobileOpen(false);
  }, [isMobile, mobileOpen]);

  const handleDrawerToggle = () => {
    if (isMobile) setMobileOpen(!mobileOpen);
  };
  const handleLinkClick = () => {
    if (isMobile) setMobileOpen(false);
  };

  const handleAppStoreClick = () => {
    trackEvent(EventNames.BUTTON_CLICK, {
      button_name: "Header App Store Download",
      button_location: "header",
      store_type: STORE_TYPES.APPLE,
      destination_url: APP_STORE_URLS.APPLE_APP_STORE,
    });
    trackOutboundLink(
      APP_STORE_URLS.APPLE_APP_STORE,
      "Header App Store Download",
      STORE_TYPES.APPLE,
    );
  };

  const handlePlayStoreClick = () => {
    trackEvent(EventNames.BUTTON_CLICK, {
      button_name: "Header Play Store Download",
      button_location: "header",
      store_type: STORE_TYPES.GOOGLE,
      destination_url: APP_STORE_URLS.GOOGLE_PLAY_STORE,
    });
    trackOutboundLink(
      APP_STORE_URLS.GOOGLE_PLAY_STORE,
      "Header Play Store Download",
      STORE_TYPES.GOOGLE,
    );
  };

  const badgeLinkStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    textDecoration: "none",
  };

  const badgeImgStyle = (height: number): React.CSSProperties => ({
    height,
    width: "auto",
    display: "block",
    transform: `translateY(${Math.round(height * BADGE_VISUAL_OFFSET_RATIO)}px)`,
  });

  const drawer = (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          padding: 8,
        }}
      >
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon style={{ color: textColorPrimary }} />
        </IconButton>
      </div>
      <List sx={{ padding: 2 }}>
        {navItems.map((item) => {
          const active = location.pathname === item.to;
          return (
            <ListItem key={item.to} disablePadding sx={{ mb: 2 }}>
              <Link
                to={item.to}
                onClick={handleLinkClick}
                style={{
                  textDecoration: "none",
                  fontFamily: fontInter,
                  fontSize: "16px",
                  fontWeight: 400,
                  color: active ? textColorActive : textColorMuted,
                  width: "100%",
                  display: "block",
                  padding: "8px 16px",
                  borderLeft: active
                    ? `2px solid ${textColorActive}`
                    : "2px solid transparent",
                }}
              >
                <ListItemText primary={item.label} />
              </Link>
            </ListItem>
          );
        })}
        <ListItem
          disablePadding
          sx={{ mt: 2, display: "flex", gap: 1, px: 2 }}
        >
          <a
            href={APP_STORE_URLS.APPLE_APP_STORE}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              handleAppStoreClick();
              handleLinkClick();
            }}
            aria-label="Download on the App Store"
            title="Download on the App Store"
            style={badgeLinkStyle}
          >
            <img
              src={appStoreBadge}
              alt="Download on the App Store"
              style={badgeImgStyle(BADGE_HEIGHT_DRAWER)}
            />
          </a>
          <a
            href={APP_STORE_URLS.GOOGLE_PLAY_STORE}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              handlePlayStoreClick();
              handleLinkClick();
            }}
            aria-label="Get it on Google Play"
            title="Get it on Google Play"
            style={badgeLinkStyle}
          >
            <img
              src={playStoreBadge}
              alt="Get it on Google Play"
              style={badgeImgStyle(BADGE_HEIGHT_DRAWER)}
            />
          </a>
        </ListItem>
      </List>
    </div>
  );

  return (
    <>
      <HeaderRoot>
        <Pill pillBg={pillBg} pillBorder={pillBorder} pillShadow={pillShadow}>
          <Link
            to="/"
            style={{ textDecoration: "none", display: "inline-flex" }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                pl: { md: "10px" },
              }}
            >
              <img
                src={logoImage}
                alt="EON.HEALTH"
                style={{
                  width: 16,
                  height: 22,
                  marginRight: 8,
                  filter: logoFilter,
                }}
              />
              <Box
                sx={{
                  fontFamily: fontInter,
                  fontSize: "17px",
                  fontWeight: 600,
                  letterSpacing: "0.005em",
                  color: textColorPrimary,
                }}
              >
                EON.HEALTH
              </Box>
            </Box>
          </Link>

          {!isMobile && (
            <>
              <Box
                component="nav"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { md: 2.5, lg: 3 },
                  ml: 2,
                }}
              >
                {navItems.map((item) => {
                  const active = location.pathname === item.to;
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      style={{ textDecoration: "none" }}
                    >
                      <Box
                        sx={{
                          fontFamily: fontInter,
                          fontSize: "16px",
                          fontWeight: 500,
                          color: active ? textColorActive : textColorMuted,
                          transition: "color 200ms ease",
                          "&:hover": { color: textColorActive },
                        }}
                      >
                        {item.label}
                      </Box>
                    </Link>
                  );
                })}
              </Box>

              <Box
                sx={{
                  ml: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <a
                  href={APP_STORE_URLS.APPLE_APP_STORE}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleAppStoreClick}
                  aria-label="Download on the App Store"
                  title="Download on the App Store"
                  style={badgeLinkStyle}
                >
                  <img
                    src={appStoreBadge}
                    alt="Download on the App Store"
                    style={badgeImgStyle(BADGE_HEIGHT_DESKTOP)}
                  />
                </a>
                <a
                  href={APP_STORE_URLS.GOOGLE_PLAY_STORE}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handlePlayStoreClick}
                  aria-label="Get it on Google Play"
                  title="Get it on Google Play"
                  style={badgeLinkStyle}
                >
                  <img
                    src={playStoreBadge}
                    alt="Get it on Google Play"
                    style={badgeImgStyle(BADGE_HEIGHT_DESKTOP)}
                  />
                </a>
              </Box>
            </>
          )}

          {isMobile && (
            <IconButton
              aria-label="open drawer"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{ color: textColorPrimary, ml: "auto" }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Pill>
      </HeaderRoot>

      {isMobile && (
        <Drawer
          variant="temporary"
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              backgroundColor: isLightBackground ? "#fff8f2" : "#111214",
              width: "100%",
              maxWidth: "300px",
            },
          }}
        >
          {drawer}
        </Drawer>
      )}
    </>
  );
};

export default Header;
